import React from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts/highstock';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import moment from 'moment';
import _ from 'lodash';

import withChartExport from '../../../components/ChartExporting';
import dateUtils, { formatWorkDuration } from '../../../util/dates';
import dataUtils from '../../../util/dataUtils';
import { isWhite } from '../../../util/colors';
import { escapeHTML } from '../../../util/text';

import highchartRegression from '../../../util/highcharts-regression';
import highchartFactory from '../../../util/highcharts';
import chartUtils from '../../../util/chartUtils';
import { WALLBOARD_TEXT_SIZE } from '../../../util/wallboardFrame';
const highchartUtils = highchartFactory(Highcharts);

// a tooltip row: 18px line, 6px padding above and below, 1px border (_diagram.scss)
const SEGMENT_ROW_HEIGHT = 31;

let chartConfig = {
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: null,
    type: 'column',
    zoomType: 'xy',
    spacingBottom: 10,
    marginRight: 10,
    events: {
      load: e => {
        e.target.options.exporting.sourceHeight = e.target.chartHeight;
        e.target.options.exporting.sourceWidth = e.target.chartWidth;
      }
    }
  },
  title: {
    text: ''
  },
  navigator: {
    enabled: false
  },
  rangeSelector: {
    enabled: false
  },
  xAxis: {
    type: 'datetime',
    ordinal: false,
    lineColor: chartUtils.TREND_AXIS_LINE_COLOR,
    tickColor: chartUtils.TREND_AXIS_LINE_COLOR,
    labels: {
      style: {
        color: chartUtils.TREND_LABEL_COLOR,
        fontSize: '12px'
      }
    },
    tickmarkPlacement: 'on',
    title: {
      enable: false
    }
  },
  yAxis: {
    opposite: false,
    allowDecimals: false,
    labels: {
      formatter: function() {
        return chartUtils.withUnit(this.value, 'item');
      },
      style: {
        color: chartUtils.TREND_LABEL_COLOR,
        fontSize: '12px',
        whiteSpace: 'nowrap'
      }
    },
    min: 0,
    stackLabels: {
      enabled: false,
      crop: true,
      style: {
        color: '#001A33',
        fontSize: '11px',
        fontWeight: '600',
        textOutline: 'none'
      }
    },
    gridLineColor: chartUtils.TREND_GRID_COLOR,
    gridLineWidth: 1,
    title: {
      text: 'Daily throughput',
      style: {
        fontSize: '13px'
      }
    }
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    series: {
      shadow: false,
      stickyTracking: false,
      cursor: 'pointer',
      point: {
        events: {}
      }
    },
    spline: {
      states: {
        inactive: {
          opacity: 1
        }
      }
    },
    column: {
      stacking: 'normal',
      pointPadding: 0.12,
      borderWidth: 1,
      groupPadding: 0,
      shadow: false,
      pointRange: 24 * 3600 * 1000,
      inactiveOtherPoints: true,
      states: chartUtils.BAR_STATES,
      dataLabels: {
        enabled: false,
        ...chartUtils.segmentLabelStyle('12px'),
        formatter: function() {
          return chartUtils.segmentLabel(this);
        }
      }
    }
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    shared: true,
    hideDelay: 0,
    stickOnContact: true,
    borderRadius: 5,
    borderWidth: 0
  },
  series: [
    {
      name: 'Data',
      data: []
    }
  ]
};

export class ThroughputChart extends React.Component {
  constructor(props) {
    super(props);
    this.chartContainer = React.createRef();
    this.state = {};
  }

  componentDidMount = () => {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);
    highchartRegression(Highcharts);

    const that = this;
    chartConfig.tooltip.formatter = function() {
      return that.tooltipFormatter(this, that);
    };
    chartConfig.plotOptions.series.point.events.click = function() {
      that.onPointClick(that, this);
    };

    chartConfig.exporting = this.props.chartExporting;
    chartConfig.chart.events.render = function() {
      if (that.chart) {
        that.togglePercentage();
      }
      chartUtils.roundTops(this);
    };

    this.chart = new Highcharts['Chart'](this.chartContainer.current, chartConfig);

    this.updateTimeLabelsFormat(this.props.dateFormat);

    if (this.props.data && this.props.data.length) {
      this.updateChart();
    }

    if (this.props.wallmode) {
      this.updateChartFont();
    }
  };

  componentDidUpdate(prevProps) {
    if (prevProps.data != this.props.data) {
      this.updateChart(prevProps.data);
    }

    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateTimeLabelsFormat(this.props.dateFormat);
    }

    if (prevProps.showNavigator != this.props.showNavigator) {
      this.chart.reflow();
    }

    if (prevProps.breakdown != this.props.breakdown) {
      this.togglePercentage();
    }

    if (prevProps.chartExporting != this.props.chartExporting) {
      this.chart.update(
        {
          exporting: {
            filename: this.props.chartExporting.filename,
            chartOptions: {
              title: {
                text: 'Throughput Run Chart',
                style: {
                  fontSize: '16px',
                  fontWeight: 'bold'
                }
              },
              plotOptions: {
                column: {
                  dataLabels: {
                    style: {
                      textOutline: 'none'
                    }
                  }
                }
              }
            }
          }
        },
        false
      );
    }

    if (prevProps.trendLine != this.props.trendLine) {
      this.updateRegression();
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  updateChart(oldData = []) {
    this.chart.xAxis[0].setExtremes(null, null);
    const diff = dataUtils.getDiff(oldData, this.props.data, 'data');
    highchartUtils.updateSeries(diff, this.chart);

    this.updateTimeUnit(this.props.timeUnit);
    this.chart.redraw();
    // Sum visible column series at each x to get true bar heights (handles stacked charts)
    const columnSeries = this.chart.series.filter(s => s.type === 'column' && s.visible);
    const stackTotals = {};
    columnSeries.forEach(s => {
      (s.xData || []).forEach((x, i) => {
        stackTotals[x] = (stackTotals[x] || 0) + ((s.yData || [])[i] || 0);
      });
    });
    const highestBar = Math.max(0, ...Object.values(stackTotals));
    this.chart.yAxis[0].update({ tickInterval: chartUtils.barTickInterval(highestBar) }, false);
    this.chart.xAxis[0].setExtremes(null,null);
    this.togglePercentage();
    this.updateRegression();
  }

  togglePercentage() {
    let enabled = false;
    if (this.props.breakdown && this.chart.series.length && this.chart.series[0].columnMetrics) {
      enabled = chartUtils.labelFits(this.chart, chartUtils.SEGMENT_LABEL_SAMPLE, '11px', '600', this.chart.series[0].columnMetrics.width);
    }

    if (this.chart.options.plotOptions.column.dataLabels.enabled === enabled) {
      return;
    }
    this.chart.update({
      plotOptions: {
        column: {
          dataLabels: {
            enabled
          }
        }
      },
      yAxis: {
        stackLabels: {
          enabled
        }
      }
    });
  }

  updateRegression() {
    const trend = _.find(this.chart.series, { name: 'trend' });
    if (trend) {
      this.chart.series[trend.index].remove(true);
    }
    if (this.props.trendLine) {
      const data = _([...this.props.data])
        .flatMap('data')
        .groupBy('x')
        .map((points, x) => {
          return {
            x: +x,
            y: _.reduce(
              points,
              (acc, p) => {
                return acc + p.y;
              },
              0
            )
          };
        })
        .value();

      this.chart.addSeries(
        {
          data,
          visible: false,
          regression: true,
          regressionSettings: {
            name: 'trend',
            useAllSeries: false,
            type: 'polynomial',
            order: 6,
            color: 'rgb(87, 181, 70)',
            lineWidth: 3,
            dashStyle: 'LongDashDot',
            tooltip: {
              enabled: false
            }
          }
        },
        true
      );
    }
  }

  updateTimeUnit(timeUnit) {
    let text = `Daily throughput`,
      days = 1,
      ordinal = true,
      placement,
      min,
      max;

    const sorted = _([...this.props.data])
      .flatMap('data')
      .orderBy('x')
      .map('x');
    min = sorted.first();
    max = sorted.last();

    if (moment(min).isSameOrAfter(this.props.dates.startDate)) {
      min = this.props.dates.startDate;
    }
    if (moment(max).isSameOrAfter(this.props.dates.endDate)) {
      max = this.props.dates.endDate;
    }

    switch (timeUnit) {
      case 'week': {
        days = 7;
        text = `Weekly throughput`;
        min = +moment(min).startOf('week');
        max = +new Date(max).endOf('week');
        break;
      }
      case '2weeks': {
        days = 14;
        text = `2 weeks throughput`;
        placement = 'between';
        min = +moment(min).startOf('week');
        max = +moment(max).add(14, 'days');
        break;
      }
      case 'month': {
        days = 30;
        ordinal = true;
        text = `Monthly throughput`;
        min = +moment(min).date(1);
        max = +moment(max).date(30);
        break;
      }
      default: {
        min = +min;
        max = +max;
        break;
      }
    }

    this.chart.update({
      xAxis: {
        ordinal
      },
      yAxis: {
        title: {
          text: text
        }
      },
      plotOptions: {
        column: {
          pointRange: days * 24 * 3600 * 1000,
          pointPlacement: placement
        }
      }
    });

    this.updateChartRange(min, max);
  }

  updateTimeLabelsFormat(dateFormat) {
    const config = { xAxis: {} };
    config.xAxis.dateTimeLabelFormats = dateUtils.createHighchartFormats(dateFormat);

    this.chart.update(config);
  }

  updateChartRange(minVal, maxVal) {
    const min = minVal || +this.props.dates.startDate;
    const max = maxVal || +this.props.dates.endDate;

    const xExtremes = {
      min,
      max
    };
    this.setExtremes(xExtremes);
  }

  updateChartFont() {
    const newConfig = highchartUtils.getFontSizeConfig('trc');
    this.chart.update({
      ...newConfig.chart,
      // the TV's larger axis labels take the room they need
      chart: { marginLeft: null },
      plotOptions: { column: { dataLabels: { style: { fontSize: WALLBOARD_TEXT_SIZE } } } }
    });
  }

  setExtremes(xExtremesObject, yExtremesObject) {
    if (xExtremesObject) {
      this.chart.xAxis[0].setExtremes(xExtremesObject.min, xExtremesObject.max);
    }
    if (yExtremesObject) {
      this.chart.yAxis[0].setExtremes(yExtremesObject.min, yExtremesObject.max);
    } else {
      this.chart.yAxis[0].setExtremes(null, null);
    }
  }

  getDateString(date) {
    return chartUtils.periodTitle(date, this.props.timeUnit, this.props.dateFormat);
  }

  tooltipFormatter(chartScope, componentScope) {
    let details = '',
      total = 0,
      totalCt = 0;
    for (let p = 0; p < chartScope.points.length; p++) {
      if (chartScope.points[p].point.details) {
        total += chartScope.points[p].y;
        totalCt += chartScope.points[p].point.cycleTime;
        for (let i = 0; i < chartScope.points[p].point.details.length; i++) {
          let point = chartScope.points[p].point.details[i];
          let color = point.color || chartScope.points[p].point.color;
          const isBgWhite = isWhite(color) ? 'border: 1px solid #00408026;' : '';
          details += `<div class="tooltip-list-item"><div class="tooltip-circle-dot" style="${isBgWhite} background: ${color}"></div>${escapeHTML(point.card.name)}</div>`;
        }
      }
    }

    const totalDuration = formatWorkDuration(totalCt, componentScope.props.excludeWeekendOpts, componentScope.props.timePrecision, componentScope.props.useWorkingHoursInCycleTimeDays);
    const date = componentScope.getDateString(chartScope.x);

    const segments = chartScope.points.length > 1
      ? chartScope.points.filter(point => point.y > 0 && _.isFinite(point.percentage))
      : [];
    const segmentRows = segments.map(point => {
      const isBgWhite = isWhite(point.color) ? 'border: 1px solid #00408026;' : '';
      return `<div class="tooltip-list-item"><div class="tooltip-circle-dot" style="${isBgWhite} background: ${point.color}"></div>${escapeHTML(point.series.name)}: ${point.percentage.toFixed(2)}% · ${point.y}</div>`;
    }).join('');

    let tooltip = `<p class="tooltip-label-title-size" style="margin: 8px 8px 0;">${date}</p>
      <p class="tooltip-label-size" style="margin: 4px 8px 0;">Cycle time: ${totalDuration}</p>
      <p class="tooltip-label-size" style="margin: 4px 8px 0;">Throughput: ${total}</p>${segmentRows}`;
   
    const detailsMaxHeight = Math.max((componentScope.chart ? componentScope.chart.chartHeight : 400) - 130 - segments.length * SEGMENT_ROW_HEIGHT, 60);
    tooltip += `<div class="tooltip-list-scroll" style="max-height: ${detailsMaxHeight}px;">${details}</div>`;
    tooltip += `<p class="tooltip-info-txt">Click on the bar to see the details</p>`;

    return tooltip;
  }

  onPointClick(comp, ev) {
    const points = [];
    const date = comp.getDateString(ev.x);
    let total = 0,
      totalCt = 0;

    for (let s = 0; s < this.chart.series.length; s++) {
      for (let p = 0; !!this.chart.series[s].points && p < this.chart.series[s].points.length; p++) {
        if (ev.x == this.chart.series[s].points[p].x && this.chart.series[s].points[p].details) {
          total += this.chart.series[s].points[p].y;
          totalCt += this.chart.series[s].points[p].cycleTime;

          for (let i = 0; i < this.chart.series[s].points[p].details.length; i++) {
            const point = this.chart.series[s].points[p].details[i];
            points.push({
              card: point.card,
              details: point.details,
              color: point.color || this.chart.series[s].color
            });
          }
        }
      }
    }

    const totalDuration = formatWorkDuration(totalCt, comp.props.excludeWeekendOpts, comp.props.timePrecision, comp.props.useWorkingHoursInCycleTimeDays);

    comp.props.onPointClick({
      date,
      value: `${total} item${total != 1 ? 's' : ''}`,
      value2: totalDuration,
      points
    });
  }

  render() {
    return (
      <div className="chart_content chart_tooltip_style">
        <div ref={this.chartContainer} className={'chart_details chart_details-full no-stroke'}></div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    dates: state.filters.dates,
    dateFormat: state.filters.dateFormat,
    timePrecision: state.filters.timePrecision,
    timeUnit: state.filters.trc.timeUnit,
    showNavigator: state.filters.trc.showNavigator,
    trendLine: state.filters.trc.trendLine,
    breakdown: state.filters.trc.breakdown,
    wallmode: state.app.wallmode,
    excludeWeekendOpts: {
      type: state.filters.excludeWeekends,
      workStart: state.filters.workStart,
      workEnd: state.filters.workEnd,
      holidays: state.filters.holidays
    },
    useWorkingHoursInCycleTimeDays: state.auth.user.config && state.auth.user.config.useWorkingHoursInCycleTimeDays
  };
};

export default withChartExport(connect(mapStateToProps)(ThroughputChart));
